import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    FaBookOpen,
    FaChevronRight,
    FaFlask,
    FaHandHoldingHeart,
    FaHeart,
    FaLaptop,
    FaMapMarkerAlt,
    FaStethoscope,
    FaTimes,
} from 'react-icons/fa';
import styles from './nutri_marimurra.module.scss';
import Perfil from './src/perfil.jpg';
import { WHATSAPP_ONLINE, WHATSAPP_PRESENCIAL, getWhatsappUrl } from './dados_nutri_marimurra.ts';

type LinkItem = {
    titulo: string;
    descricao: string;
    observacao?: string;
    icon: React.ReactNode;
    href?: string;
    to?: string;
    abreModalCursos?: boolean;
};

const LINKS: LinkItem[] = [
    {
        titulo: 'Agendar consulta online',
        descricao: 'Por videochamada, de onde você estiver',
        observacao: 'Para pacientes fora de Barretos',
        icon: <FaLaptop />,
        href: getWhatsappUrl(
            WHATSAPP_ONLINE,
            'Olá, Mariana! Vim pelo Instagram e gostaria de agendar uma consulta online.'
        ),
    },
    {
        titulo: 'Agendar consulta presencial',
        descricao: 'Atendimento no consultório',
        observacao: 'No Instituto Crescer com Afeto (ICCA) em Barretos',
        icon: <FaMapMarkerAlt />,
        href: getWhatsappUrl(
            WHATSAPP_PRESENCIAL,
            'Olá, Mariana! Vim pelo Instagram e gostaria de agendar uma consulta presencial.'
        ),
    },
    {
        titulo: 'Terapia alimentar',
        descricao: 'Quando comer vira um desafio para o seu filho',
        icon: <FaHandHoldingHeart />,
        to: '/nutri_marimurra/terapia-alimentar',
    },
    {
        titulo: 'Cursos e materiais',
        descricao: 'Cursos, e-books e aulas',
        icon: <FaBookOpen />,
        abreModalCursos: true,
    },
    {
        titulo: 'Conteúdos',
        descricao: 'Currículo, artigos e entrevistas',
        icon: <FaFlask />,
        to: '/nutri_marimurra/conteudos',
    },
];

const OPCOES_CURSOS = [
    {
        titulo: 'Para profissionais de saúde e nutrição',
        icon: <FaStethoscope />,
        to: '/nutri_marimurra/cursos/profissionais',
    },
    {
        titulo: 'Para familiares e cuidadores',
        icon: <FaHeart />,
        to: '/nutri_marimurra/cursos/familias',
    },
];

const Nutri_marimurra: React.FC = () => {
    const [modalCursosAberto, setModalCursosAberto] = useState(false);

    useEffect(() => {
        if (!modalCursosAberto) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setModalCursosAberto(false);
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [modalCursosAberto]);

    return (
        <div className={styles.nutri_marimurra}>
            <div className={styles.page}>
                <div className={styles.hero}>
                    <img className={styles.heroImg} src={Perfil} alt="Mariana Murra, nutricionista" />
                    <div className={styles.heroFade} />
                </div>

                <div className={styles.intro}>
                    <p className={styles.eyebrow}>Nutricionista</p>
                    <h1 className={styles.name}>Mariana Murra</h1>
                    <p className={styles.credentials}>Pesquisadora | Mestre em Oncologia</p>
                    <span className={styles.divider} />
                    <p className={styles.tagline}>
                        Apresento <em>ciência e leveza</em> na alimentação infantil
                    </p>
                </div>

                <nav className={styles.links}>
                    {LINKS.map((item) => {
                        const conteudo = (
                            <>
                                <span className={styles.linkIcon}>{item.icon}</span>
                                <span className={styles.linkText}>
                                    <span className={styles.linkTitle}>{item.titulo}</span>
                                    <span className={styles.linkDesc}>{item.descricao}</span>
                                    {item.observacao ? (
                                        <span className={styles.linkObs}>{item.observacao}</span>
                                    ) : null}
                                </span>
                                <FaChevronRight className={styles.linkArrow} />
                            </>
                        );

                        if (item.abreModalCursos) {
                            return (
                                <button
                                    key={item.titulo}
                                    type="button"
                                    className={styles.link}
                                    onClick={() => setModalCursosAberto(true)}
                                >
                                    {conteudo}
                                </button>
                            );
                        }

                        return item.to ? (
                            <Link key={item.titulo} to={item.to} className={styles.link}>
                                {conteudo}
                            </Link>
                        ) : (
                            <a
                                key={item.titulo}
                                href={item.href}
                                className={styles.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {conteudo}
                            </a>
                        );
                    })}
                </nav>

                <footer className={styles.footer}>Mariana Murra · Nutrição Infantil</footer>
            </div>

            {modalCursosAberto ? (
                <div className={styles.modalOverlay} onClick={() => setModalCursosAberto(false)}>
                    <div
                        className={styles.modal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="modal-cursos-titulo"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className={styles.modalClose}
                            onClick={() => setModalCursosAberto(false)}
                            aria-label="Fechar"
                        >
                            <FaTimes />
                        </button>
                        <p className={styles.eyebrow}>Cursos e materiais</p>
                        <h2 id="modal-cursos-titulo" className={styles.modalTitle}>
                            Para quem é?
                        </h2>
                        <div className={styles.modalOptions}>
                            {OPCOES_CURSOS.map((opcao) => (
                                <Link key={opcao.to} to={opcao.to} className={styles.modalOption}>
                                    <span className={styles.linkIcon}>{opcao.icon}</span>
                                    <span className={styles.modalOptionText}>{opcao.titulo}</span>
                                    <FaChevronRight className={styles.linkArrow} />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default Nutri_marimurra;
